(() => {
  if (window.__FURMILY_PERF_LIGHT__) return;
  window.__FURMILY_PERF_LIGHT__ = true;

  const animationSelector = '.fbgp-track, .fm-ticker-track, .fs__track';
  const watchedTracks = new WeakSet();
  const watchedFrames = new WeakSet();
  const watchedImages = new WeakSet();

  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;

  const getImageAheadProfile = () => {
    const effectiveType = String(connection?.effectiveType || '').toLowerCase();
    const saveData = Boolean(connection?.saveData);

    if (saveData || effectiveType === 'slow-2g' || effectiveType === '2g') {
      return {
        rootMargin: '500px 280px',
        idleTimeout: 2600,
      };
    }

    if (effectiveType === '3g') {
      return {
        rootMargin: '1000px 650px',
        idleTimeout: 2200,
      };
    }

    if (effectiveType === '4g') {
      return {
        rootMargin: '1800px 1200px',
        idleTimeout: 1600,
      };
    }

    return {
      rootMargin: '1300px 850px',
      idleTimeout: 1900,
    };
  };

  const imageAheadProfile = getImageAheadProfile();

  const warmImage = (img) => {
    if (!img || img.dataset.fmImageWarm === 'true') return;

    const hasSource =
      Boolean(img.currentSrc) ||
      Boolean(img.getAttribute('src')) ||
      Boolean(img.getAttribute('srcset'));

    if (!hasSource) return;

    img.dataset.fmImageWarm = 'true';

    if (img.getAttribute('loading') === 'lazy' || img.loading === 'lazy') {
      img.loading = 'eager';
      img.setAttribute('loading', 'eager');
    }

    const priority = img.getAttribute('fetchpriority');
    if (!priority || priority === 'low') {
      try {
        img.fetchPriority = 'auto';
      } catch {}
      img.setAttribute('fetchpriority', 'auto');
    }

    if (!img.hasAttribute('decoding')) {
      img.decoding = 'async';
      img.setAttribute('decoding', 'async');
    }

    if (!img.complete && typeof img.decode === 'function') {
      img.decode().catch(() => {});
    }
  };

  const imageObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          warmImage(entry.target);
          imageObserver.unobserve(entry.target);
        }
      }, {
        rootMargin: imageAheadProfile.rootMargin,
        threshold: 0.01,
      })
    : null;

  const trackObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          entry.target.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
        }
      }, { rootMargin: '300px 0px' })
    : null;

  const registerImage = (img) => {
    if (!img || watchedImages.has(img)) return;

    const hasSource =
      Boolean(img.currentSrc) ||
      Boolean(img.getAttribute('src')) ||
      Boolean(img.getAttribute('srcset'));

    if (!hasSource) return;

    watchedImages.add(img);

    if (!img.hasAttribute('decoding')) {
      img.decoding = 'async';
      img.setAttribute('decoding', 'async');
    }

    if (img.complete && img.naturalWidth > 0) return;

    const priority = img.getAttribute('fetchpriority');
    const immediate =
      img.getAttribute('loading') === 'eager' ||
      img.loading === 'eager' ||
      priority === 'high';

    if (immediate || !imageObserver) {
      warmImage(img);
      return;
    }

    imageObserver.observe(img);
  };

  const tune = (scope = document) => {
    const root = scope?.querySelectorAll ? scope : document;

    root.querySelectorAll?.('img').forEach(registerImage);

    root.querySelectorAll?.('iframe').forEach((frame) => {
      if (watchedFrames.has(frame)) return;
      watchedFrames.add(frame);
      if (!frame.hasAttribute('loading')) frame.loading = 'lazy';
    });

    if (trackObserver) {
      root.querySelectorAll?.(animationSelector).forEach((track) => {
        if (watchedTracks.has(track)) return;
        watchedTracks.add(track);
        track.style.animationPlayState = 'paused';
        trackObserver.observe(track);
      });
    }
  };

  const idleRetune = () => {
    const run = () => tune(document);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: imageAheadProfile.idleTimeout });
    } else {
      window.setTimeout(run, Math.min(900, imageAheadProfile.idleTimeout));
    }
  };

  const start = () => {
    tune(document);
    if (document.readyState === 'complete') idleRetune();
    else window.addEventListener('load', idleRetune, { once: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', () => tune(document));
  document.addEventListener('shopify:section:load', (event) => {
    tune(event.target || document);
    idleRetune();
  });
})();